import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import kuzniaLogo from '@/assets/kil_logo.webp';
import heroBieg from '@/assets/hero-bieg-mikolajkowy.webp';
import heroDL from '@/assets/hero-dzialaj-lokalnie.webp';
import heroIntegracja from '@/assets/hero-integracja.webp';
import logoDL from '@/assets/logo-dzialaj-lokalnie.webp';
import logoPAFW from '@/assets/logo-pafw.png';
import logoAkademia from '@/assets/logo-akademia.webp';
import { useOdlCount } from '@/hooks/useOdlCount';
import { getHeroStats } from '@/config/heroStats';
import { useDlResults } from '@/hooks/useDlResults';
import { safeHref } from '@/lib/safeUrl';



const heroTiles = [
  { label: 'Bieg Mikołajkowy', img: heroBieg },
  { label: 'Działaj Lokalnie', img: heroDL },
  { label: 'Integracja', img: heroIntegracja },
];

function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function useCountUp(end: number, duration = 2000) {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(end);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setStarted(true);
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end]);

  useEffect(() => {
    if (!started) return;
    // Pomiń animację dla małych liczb (np. „1 z 77") — uniknij utykania na 0.
    if (end <= 5) {
      setValue(end);
      return;
    }
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= end) {
        setValue(end);
        clearInterval(interval);
      } else {
        setValue(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [started, end, duration]);


  return { value, ref };
}

const CounterTile = ({ end, suffix, label }: { end: number; suffix: string; label: string }) => {
  const { value, ref } = useCountUp(end);
  return (
    <div ref={ref} className="text-center">
      <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary font-heading whitespace-nowrap">
        {value.toLocaleString('pl-PL')}{suffix}
      </p>
      <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 leading-tight">{label}</p>
    </div>
  );
};

const HeroSection = () => {
  const odlCount = useOdlCount();
  const counters = getHeroStats(odlCount);
  const dl = useDlResults();
  return (
    <section className="relative overflow-hidden bg-background">
      <div className="container py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* LEFT */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <img src={kuzniaLogo} alt="Logo Kuźnia Inicjatyw Lokalnych" className="h-16 w-auto" width={200} height={64} {...({ fetchpriority: 'high' } as Record<string, string>)} decoding="async" />
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
              {counters.map((c, i) => (
                <CounterTile key={i} end={c.value} suffix={c.suffix ?? ''} label={c.label} />
              ))}
            </div>


            {dl.enabled && dl.badge_text.trim() && (
              <a
                href={safeHref(dl.url, '#')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold mb-6 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#f5c800', color: '#1a1a1a' }}
              >
                <span aria-hidden="true">✅</span>
                <span>{dl.badge_text}</span>
              </a>
            )}


            <h1 className="text-3xl lg:text-5xl font-heading font-bold leading-tight mb-6">
              Wyzwalamy <em className="text-primary not-italic">społeczną energię</em> do&nbsp;zmiany
            </h1>

            <p className="text-muted-foreground mb-8 max-w-lg leading-relaxed">
              Wspieramy mieszkańców, organizujemy konkursy grantowe i realizujemy projekty edukacyjne,
              kulturalne i społeczne — od 2003 roku w sercu Ziemi Chełmińskiej.
            </p>

            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                <a href="https://generatorspoleczny.pl/" target="_blank" rel="noopener noreferrer" aria-label="Złóż wniosek o grant (otwiera się w nowej karcie)">Złóż wniosek o grant</a>
              </Button>
              <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold">
                <a href="#aktualnosci">Aktualności</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-foreground/20 hover:bg-muted font-semibold">
                <a href="#o-nas">Poznaj Kuźnię</a>
              </Button>
            </div>
          </div>

          {/* RIGHT */}
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              {heroTiles.map((tile, i) => (
                <div key={i} className="relative rounded-lg overflow-hidden aspect-square group">
                  <img
                    src={tile.img}
                    alt={tile.label}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                    width={640}
                    height={640}
                  />
                  <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <p className="absolute bottom-2 left-2 font-heading font-bold text-sm text-white bg-black/60 px-2 py-1 rounded">
                    {tile.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-card border rounded-panel p-6 shadow-sm">
              <span className="inline-block bg-accent/20 text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full mb-3">
                Program grantowy 2025
              </span>
              <h2 className="font-heading font-bold text-lg mb-4">Działaj Lokalnie — weź grant na swój pomysł</h2>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="font-bold text-secondary text-base sm:text-lg whitespace-nowrap">6 000 zł</p>
                  <p className="text-xs text-muted-foreground">max kwota</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="font-bold text-secondary text-base sm:text-lg">4</p>
                  <p className="text-xs text-muted-foreground">gminy</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="font-bold text-secondary text-base sm:text-lg">IX</p>
                  <p className="text-xs text-muted-foreground">edycja</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
                <span className="shrink-0">Partnerzy:</span>
                <a href="https://www.dzialajlokalnie.pl" target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                  <img src={logoDL} alt="Działaj Lokalnie" width={120} height={32} className="h-8 w-auto" />
                </a>
                <a href="https://www.pafw.pl" target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                  <img src={logoPAFW} alt="PAFW" width={120} height={32} className="h-8 w-auto" />
                </a>
                <a href="https://www.filantropia.org.pl" target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                  <img src={logoAkademia} alt="Akademia Filantropii" width={120} height={32} className="h-8 w-auto" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
